// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectoryView } from "filemanager/DirectoryFilesView";
import { ErrorFilesView } from "filemanager/ErrorFilesView";
import { DgDirectory, DgFile, DirectoryStatus, FileManagerInstance } from "filemanager/FileManager";
import { useEffect, useRef, useState } from "react";
import { ObservableChange } from "utils/ObservableArray";
import { RequestState, useAsyncHelper } from "./AsyncHelper";

export interface IFileView<T> {
    isLoaded: boolean;
    items: T
}

export function useDirectoryView(path: string, showHidden: boolean = false): IFileView<Array<DgFile | DgDirectory>> {
    const [isLoaded, setIsLoaded] = useState(false);
    const [items, setItems] = useState<Array<DgFile | DgDirectory>>([]);
    const viewRef = useRef<DirectoryView>();
    const { execute, state } = useAsyncHelper<any>("Directory View", path);

    useEffect(() => {
        viewRef.current = new DirectoryView(FileManagerInstance, path, showHidden);
        const unregister = viewRef.current.observableItems.registerListener(change => onViewChanged(change));

        setIsLoaded(false);
        setItems([]);
        execute(viewRef.current.initialize());

        return () => unregister();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [path]);

    useEffect(() => {
        if (state === RequestState.Complete) {
            // Update mapped files/dirs
            updateFilesAndFolders();
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state])

    function onViewChanged(change: ObservableChange<DgDirectory | DgFile>) {
        if (viewRef.current!.directory.status === DirectoryStatus.Initialized) {
            // Update our mapped files/dirs
            updateFilesAndFolders();
        }
    }

    function updateFilesAndFolders() {
        const newItems = viewRef.current?.items ?? [];
        setItems(newItems);
        setIsLoaded(true);
    }

    return {
        isLoaded: isLoaded,
        items: items
    };
}

export function useErrorFiles(): IFileView<Array<DgFile>> {
    const [isLoaded, setIsLoaded] = useState(false);
    const [items, setItems] = useState<Array<DgFile>>([]);
    const viewRef = useRef<ErrorFilesView>();
    const { execute, state } = useAsyncHelper<any>("Error Files View");

    useEffect(() => {
        viewRef.current = new ErrorFilesView(FileManagerInstance);
        const unregister = viewRef.current.observableItems.registerListener(change => onViewChanged(change));

        setIsLoaded(false);
        setItems([]);
        execute(viewRef.current.initialize());

        return () => unregister();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    useEffect(() => {
        if (state === RequestState.Complete) {
            // Update mapped files/dirs
            updateFilesAndFolders();
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state])

    function onViewChanged(change: ObservableChange<DgFile>) {
        if (viewRef.current!.initialized) {
            updateFilesAndFolders();
        }
    }

    function updateFilesAndFolders() {
        setItems(viewRef.current?.items ?? []);
        setIsLoaded(true);
    }

    return {
        isLoaded: isLoaded,
        items: items
    };
}
